/**
 * OrgReviewsSection — Depoimentos + Corações (iter282)
 * Aparece no single da ONG. Só usuários logados podem publicar.
 * Dono da ONG não vê o formulário (não pode se auto-avaliar).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Heart, Pencil, Trash2, Send, User as UserIcon, LogIn } from 'lucide-react';
import { api } from '../services/api';
import AuthModal from './AuthModal';
import { toast } from 'sonner';

function HeartsPicker({ value, onChange, size = 'w-7 h-7', readOnly = false, testId = 'hearts-picker' }) {
  const [hover, setHover] = useState(0);
  const active = hover || value || 0;
  return (
    <div className="flex gap-1" role="radiogroup" aria-label="Corações" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange(n)}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          className={`transition-all ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} disabled:cursor-default`}
          aria-label={`${n} coração${n > 1 ? 'es' : ''}`}
          data-testid={`heart-${n}`}
        >
          <Heart
            className={`${size} transition-colors ${
              n <= active ? 'text-pink-500 fill-pink-500' : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewItem({ item, onEdit, onDelete }) {
  return (
    <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid={`review-item-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {item.user_avatar_url ? (
              <img src={item.user_avatar_url} alt={item.user_nome} className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="w-5 h-5 text-pink-500" />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{item.user_nome}</p>
            <p className="text-xs text-gray-500">
              {item.updated_at ? 'editado em ' : ''}
              {new Date(item.updated_at || item.created_at).toLocaleDateString('pt-BR', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}
            </p>
          </div>
        </div>
        <HeartsPicker value={item.hearts} readOnly size="w-4 h-4" testId={`review-hearts-${item.id}`} />
      </div>
      <p className="mt-3 text-gray-700 whitespace-pre-wrap break-words">{item.depoimento}</p>
      {item.is_owner_can_edit && (
        <div className="mt-2 flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => onEdit(item)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-purple-600 rounded"
            data-testid={`review-edit-${item.id}`}
          >
            <Pencil className="w-3 h-3" /> Editar
          </button>
          <button
            type="button"
            onClick={() => onDelete(item)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-red-600 rounded"
            data-testid={`review-delete-${item.id}`}
          >
            <Trash2 className="w-3 h-3" /> Apagar
          </button>
        </div>
      )}
    </div>
  );
}

export default function OrgReviewsSection({ orgId, currentUser, isOwner }) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [myReview, setMyReview] = useState(null);
  const [stats, setStats] = useState({ hearts_avg: 0, hearts_count: 0, distribuicao: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formHearts, setFormHearts] = useState(5);
  const [formText, setFormText] = useState('');
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get(`/orgs/${orgId}/reviews?limit=50`),
        api.get(`/orgs/${orgId}/reviews/stats`),
      ]);
      setItems(list.data.items || []);
      setTotal(list.data.total || 0);
      setMyReview(list.data.my_review || null);
      setStats(st.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const canPublish = !!currentUser && !isOwner;

  const openEdit = (item) => {
    setFormHearts(item.hearts);
    setFormText(item.depoimento);
    setEditing(true);
    setTimeout(() => document.getElementById('org-review-textarea')?.focus(), 50);
  };

  const cancelEdit = () => {
    setEditing(false);
    setFormText('');
    setFormHearts(5);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!formText.trim() || formText.trim().length < 10) {
      toast.error('O depoimento precisa ter pelo menos 10 caracteres.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/orgs/${orgId}/reviews`, {
        hearts: formHearts,
        depoimento: formText.trim(),
      });
      toast.success(res.data.created ? 'Depoimento publicado!' : 'Depoimento atualizado!');
      setEditing(false);
      setFormText('');
      setFormHearts(5);
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao publicar depoimento.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Apagar seu depoimento? Essa ação não pode ser desfeita.')) return;
    try {
      await api.delete(`/orgs/${orgId}/reviews/${item.id}`);
      toast.success('Depoimento apagado.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao apagar depoimento.');
    }
  };

  const displayed = useMemo(() => items, [items]);

  return (
    <div data-testid="org-reviews-section">
      {/* Resumo topo */}
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Heart className="w-6 h-6 text-pink-500 fill-pink-500" />
            <span className="text-2xl font-bold text-gray-900" data-testid="hearts-avg">
              {stats.hearts_avg ? stats.hearts_avg.toFixed(1) : '—'}
            </span>
          </div>
          <div className="text-sm text-gray-500">
            {stats.hearts_count > 0
              ? `${stats.hearts_count} depoimento${stats.hearts_count > 1 ? 's' : ''}`
              : 'Nenhum depoimento ainda'}
          </div>
        </div>
      </div>

      {/* Formulário */}
      {canPublish && (myReview == null || editing) && (
        <form
          onSubmit={handleSubmit}
          className="p-4 bg-pink-50 border border-pink-100 rounded-xl mb-4"
          data-testid="org-review-form"
        >
          <p className="text-sm font-semibold text-gray-800 mb-2">
            {myReview ? 'Editar seu depoimento' : 'Deixe seu depoimento'}
          </p>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs text-gray-600">Corações:</span>
            <HeartsPicker value={formHearts} onChange={setFormHearts} testId="form-hearts" />
          </div>
          <textarea
            id="org-review-textarea"
            value={formText}
            onChange={(e) => setFormText(e.target.value)}
            placeholder="Conta sua experiência com essa organização — o que te tocou, o que viu de impacto…"
            className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-y min-h-[100px] focus:outline-none focus:ring-2 focus:ring-pink-400"
            maxLength={1000}
            data-testid="org-review-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-400">{formText.length}/1000</span>
            <div className="flex gap-2">
              {editing && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                  data-testid="org-review-cancel"
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-1 px-4 py-1.5 bg-pink-500 text-white text-sm font-semibold rounded-lg hover:bg-pink-600 disabled:opacity-50"
                data-testid="org-review-submit"
              >
                <Send className="w-3 h-3" />
                {saving ? 'Enviando…' : (myReview ? 'Atualizar' : 'Publicar')}
              </button>
            </div>
          </div>
        </form>
      )}

      {!currentUser && (
        <div className="p-4 bg-pink-50 border border-pink-100 rounded-xl mb-4" data-testid="org-review-login-cta">
          <p className="text-sm text-gray-700 mb-3">
            Compartilhe sua experiência! Entre pra deixar um depoimento com corações sobre essa organização.
          </p>
          <button
            type="button"
            onClick={() => setShowAuthModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500 text-white text-sm font-semibold rounded-lg hover:bg-pink-600 transition-colors"
            data-testid="org-review-login-btn"
          >
            <LogIn className="w-4 h-4" />
            Entrar para deixar um depoimento
          </button>
        </div>
      )}

      {isOwner && (
        <div className="p-3 bg-yellow-50 border border-yellow-100 rounded-lg text-xs text-yellow-800 mb-4">
          Você é responsável por essa organização — não é possível se auto-avaliar.
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="text-center py-6 text-gray-400 text-sm">Carregando depoimentos…</div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          Ainda não há depoimentos. {canPublish ? 'Seja a primeira pessoa a compartilhar!' : ''}
        </div>
      ) : (
        <div className="space-y-3">
          {displayed.map((item) => (
            <ReviewItem key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {total > displayed.length && (
        <p className="text-center text-xs text-gray-400 mt-3">
          Mostrando {displayed.length} de {total} depoimentos
        </p>
      )}

      {/* Modal de login inline — não navega, mantém usuário na página da ONG */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          load();
        }}
      />
    </div>
  );
}
